import { ChatMarkdown } from '@/components/chat/ChatMarkdown';
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Upload, MessageSquare, Send, Trash2, Loader2, BookOpen, RefreshCw, ChevronDown } from "lucide-react";
import { useLocalization } from '@/hooks/useLocalization';

interface Document {
  id: string;
  title: string;
  content: string;
  created_at: string;
}

interface ChatSource {
  id: string;
  title: string;
  similarity: number;
  chunkPreview: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: ChatSource[];
}

export function KnowledgeBase() {
  const { tx, t } = useLocalization();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [query, setQuery] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isQuerying, setIsQuerying] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAdmin } = useUserRole();

  // Fetch documents
  const { data: documents = [], isLoading: documentsLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Document[];
    },
  });

  // Upload document mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ title, content }: { title: string; content: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rag-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'upload', title, content }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload document');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setTitle("");
      setContent("");
      toast({
        title: t('Document uploaded'),
        description: t('Your document has been added to the knowledge base.'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('Upload failed'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete document mutation
  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rag-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'delete', documentId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete document');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({
        title: t('Document deleted'),
        description: t('The document has been removed from the knowledge base.'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('Delete failed'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Backfill embeddings for documents uploaded before RAG was activated (admin-only)
  const reembedMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rag-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'reembed', limit: 20 }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reprocess documents');
      }

      return response.json() as Promise<{ documentsProcessed: number; chunksCreated: number; failures: string[] }>;
    },
    onSuccess: (data) => {
      toast({
        title: t('Reprocessing complete'),
        description: `Embedded ${data.documentsProcessed} document(s), ${data.chunksCreated} chunk(s) created.` +
          (data.failures.length > 0 ? ` ${data.failures.length} failed.` : ''),
        variant: data.failures.length > 0 ? "destructive" : "default",
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('Reprocessing failed'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Query knowledge base
  const handleQuery = async () => {
    if (!query.trim()) return;

    const userMessage: ChatMessage = { role: 'user', content: query };
    setChatMessages(prev => [...prev, userMessage]);
    setQuery("");
    setIsQuerying(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rag-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'query', query: userMessage.content }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to query knowledge base');
      }

      const data = await response.json();
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.answer,
        sources: data.sources,
      };
      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast({
        title: t('Query failed'),
        description: error instanceof Error ? error.message : t('Unknown error'),
        variant: "destructive",
      });
    } finally {
      setIsQuerying(false);
    }
  };

  const handleUpload = () => {
    if (!title.trim() || !content.trim()) {
      toast({
        title: t('Missing fields'),
        description: t('Please provide both title and content.'),
        variant: "destructive",
      });
      return;
    }
    uploadMutation.mutate({ title, content });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <BookOpen className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">{t('Knowledge Base')}</h1>
          <p className="text-muted-foreground">{t('Upload documents and query your company knowledge with AI')}</p>
        </div>
      </div>

      <Tabs defaultValue="chat" className="space-y-4">
        <TabsList>
          <TabsTrigger value="chat" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            
            {t('Ask Questions')}
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            
            {t('Documents (')}{documents.length})
          </TabsTrigger>
          <TabsTrigger value="upload" className="gap-2">
            <Upload className="h-4 w-4" />
            
            {t('Upload')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="space-y-4">
          <Card className="h-[500px] flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t('Query Knowledge Base')}</CardTitle>
              <CardDescription>{t('Ask questions about your uploaded documents')}</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <ScrollArea className="flex-1 pe-4 mb-4">
                {chatMessages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <div className="text-center space-y-2">
                      <MessageSquare className="h-12 w-12 mx-auto opacity-50" />
                      <p>{t('No messages yet. Ask a question to get started!')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          {msg.role === 'assistant'
                            ? <ChatMarkdown text={msg.content} />
                            : <p className="whitespace-pre-wrap">{msg.content}</p>}
                          {msg.sources && msg.sources.length > 0 && (
                            <Collapsible className="mt-2 pt-2 border-t border-border/50">
                              <CollapsibleTrigger className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100">
                                <ChevronDown className="h-3 w-3" />
                                
                                {t('Sources (')}{msg.sources.length})
                              </CollapsibleTrigger>
                              <CollapsibleContent className="mt-2 space-y-2">
                                {msg.sources.map((source) => (
                                  <div
                                    key={source.id}
                                    className="rounded-md border border-border/50 bg-background/50 p-2 text-xs"
                                  >
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <Badge variant="secondary" className="text-xs">
                                        {tx(source.title)}
                                      </Badge>
                                      {source.similarity > 0 && (
                                        <span className="opacity-60">
                                          {Math.round(source.similarity * 100)}{t('% match')}
                                        </span>
                                      )}
                                    </div>
                                    <p className="opacity-70 line-clamp-3">{source.chunkPreview}</p>
                                  </div>
                                ))}
                              </CollapsibleContent>
                            </Collapsible>
                          )}
                        </div>
                      </div>
                    ))}
                    {isQuerying && (
                      <div className="flex justify-start">
                        <div className="bg-muted rounded-lg px-4 py-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
              <div className="flex gap-2">
                <Input
                  placeholder={t('Ask a question about your documents...')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleQuery()}
                  disabled={isQuerying}
                />
                <Button onClick={handleQuery} disabled={isQuerying || !query.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle>{t('Uploaded Documents')}</CardTitle>
                <CardDescription>{t('Your knowledge base contains')} {documents.length}  {t('document(s)')}</CardDescription>
              </div>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => reembedMutation.mutate()}
                  disabled={reembedMutation.isPending}
                >
                  {reembedMutation.isPending ? (
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 me-2" />
                  )}
                  
                  {t('Reprocess embeddings')}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {documentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>{t('No documents uploaded yet.')}</p>
                  <p className="text-sm">{t('Go to the Upload tab to add your first document.')}</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-start justify-between p-4 rounded-lg border bg-card"
                      >
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">{tx(doc.title)}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {doc.content}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            
                            {t('Added')} {new Date(doc.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="ms-2 text-destructive hover:text-destructive"
                          onClick={() => deleteMutation.mutate(doc.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="upload">
          <Card>
            <CardHeader>
              <CardTitle>{t('Upload Document')}</CardTitle>
              <CardDescription>{t('Add text content to your knowledge base')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('Document Title')}</label>
                <Input
                  placeholder={t('e.g., Company Policies, Product FAQ...')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('Content')}</label>
                <Textarea
                  placeholder={t('Paste or type your document content here...')}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={12}
                  className="resize-none"
                />
              </div>
              <Button
                onClick={handleUpload}
                disabled={uploadMutation.isPending || !title.trim() || !content.trim()}
                className="w-full"
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                    
                    {t('Uploading...')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 me-2" />
                    
                    {t('Upload Document')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
